<template>
  <v-card
    flat
    class="hosting-sidebar"
    :style="{
      background: theme.background,
      color: theme.text
    }"
  >
    <!-- ===================================================== -->
    <!-- SEARCH -->
    <!-- ===================================================== -->

    <div class="sidebar-search-wrapper">
      <v-text-field
        v-model="search"
        :label="t('search')"
        variant="outlined"
        density="compact"
        clearable
        hide-details
        prepend-inner-icon="mdi-magnify"
        class="sidebar-search"
      />
    </div>

    <!-- ===================================================== -->
    <!-- MENU -->
    <!-- ===================================================== -->

    <div class="sidebar-menu">
      <button
        v-for="item in filteredMenu"
        :key="item.key"
        type="button"
        class="sidebar-item"
        :class="{
          active: isActive(item)
        }"
        @click="navigate(item)"
      >
        <v-icon
          size="21"
          class="sidebar-icon"
        >
          {{ item.icon }}
        </v-icon>

        <span class="sidebar-label">
          {{ item.title }}
        </span>

        <span
          v-if="item.badge"
          class="sidebar-badge"
        >
          {{ item.badge }}
        </span>
      </button>
    </div>
  </v-card>
</template>

<script setup>
import { computed, ref, watch } from 'vue'
import { useRoute, useRouter } from 'vue-router'
import { useI18n } from 'vue-i18n'
import { useAuthStore, useThemeStore } from '@/store/auth'
import { useLangStore } from '@/store/lang'

// ============================================================
// STORES / ROUTER
// ============================================================

const auth = useAuthStore()
const themeStore = useThemeStore()
const langStore = useLangStore()
const route = useRoute()
const router = useRouter()
const { t, locale } = useI18n()

// ============================================================
// THEME / LANGUAGE
// ============================================================

const theme = computed(() => themeStore.themeStyles)
const currentLanguage = computed(() => langStore.currentLanguage || 'en')

watch(currentLanguage, newLanguage => {
  locale.value = newLanguage
}, { immediate: true })

// ============================================================
// SEARCH
// ============================================================

const search = ref('')

// ============================================================
// MENU
// ============================================================

const menu = computed(() => [
  { key: 'dashboard', title: currentLanguage.value === 'en' ? 'Dashboard' : 'Tableau de bord', icon: 'mdi-gauge', routeName: 'SaaSHostingDashboard' },
  { key: 'expiring', title: currentLanguage.value === 'en' ? 'Expiring / Expired' : 'Expiration / Expiré', icon: 'mdi-timer-sand', routeName: null },
  { key: 'domains', title: currentLanguage.value === 'en' ? 'Domain List' : 'Liste des domaines', icon: 'mdi-home', routeName: 'SaaSHostingSubdomains' },
  { key: 'hosting', title: currentLanguage.value === 'en' ? 'Hosting List' : 'Liste des hébergements', icon: 'mdi-server', routeName: 'SaaSHostingAccount' },
  { key: 'email', title: currentLanguage.value === 'en' ? 'Private Email' : 'Courriel privé', icon: 'mdi-email-outline', routeName: "PrivateEmails" },
  { key: 'ssl', title: currentLanguage.value === 'en' ? 'SSL Certificates' : 'Certificats SSL', icon: 'mdi-lock-outline', routeName: 'SaaSHostingSsl' },
  { key: 'billing', title: currentLanguage.value === 'en' ? 'Billing & Payments' : 'Facturation et paiements', icon: 'mdi-credit-card-outline', routeName: 'SaaSHostingSubscription' },
  { key: 'activity', title: currentLanguage.value === 'en' ? 'Activity' : 'Activité', icon: 'mdi-history', routeName: 'SaaSHostingActivity' },
  { key: 'apps', title: currentLanguage.value === 'en' ? 'Apps' : 'Applications', icon: 'mdi-view-grid-outline', routeName: null },
  { key: 'offers', title: currentLanguage.value === 'en' ? 'My Offers' : 'Mes offres', icon: 'mdi-tag-outline', badge: 'NEW', routeName: null },
  { key: 'profile', title: currentLanguage.value === 'en' ? 'Profile' : 'Profil', icon: 'mdi-cog-outline', routeName: null }
])

// ============================================================
// PERMISSION FILTERING
// ============================================================

const filteredMenu = computed(() => {
  const query = search.value.trim().toLowerCase()

  return menu.value.filter(item => {
    if (item.permission && !auth.hasPermission(item.permission)) return false
    if (!query) return true

    return item.title.toLowerCase().includes(query)
  })
})

// ============================================================
// ACTIVE STATE
// ============================================================

const routeMap = {
  SaaSHostingDashboard: 'dashboard',
  SaaSHostingAccount: 'hosting',
  SaaSHostingSubdomains: 'domains',
  SaaSHostingSsl: 'ssl',
  SaaSHostingSubscription: 'billing',
  SaaSHostingActivity: 'activity'
}

const activeKey = computed(() => route.meta?.hostingMenu || routeMap[route.name] || 'dashboard')
const isActive = item => activeKey.value === item.key

// ============================================================
// NAVIGATION
// ============================================================

const navigate = async item => {
  if (!item.routeName) return

  await router.push({
    name: item.routeName,
    params: { lang: currentLanguage.value }
  })
}
</script>

<style scoped>
/* ============================================================
   ROOT
============================================================ */

.hosting-sidebar {
  width: 100%;
  min-height: 100%;

  border-radius: 0 !important;

  background: #303030 !important;

  overflow: visible;
}

/* ============================================================
   SEARCH
============================================================ */

.sidebar-search-wrapper {
  padding: 10px 8px;
}

.sidebar-search {
  width: 100%;
}

/*
 * Keep the search readable even though the menu
 * background is dark.
 */
.sidebar-search :deep(.v-field) {
  background: #ffffff;
}

.sidebar-search :deep(.v-field__input) {
  font-size: 12px;
}

/* ============================================================
   MENU
============================================================ */

.sidebar-menu {
  width: 100%;
}

.sidebar-item {
  position: relative;

  width: 100%;
  min-height: 43px;

  display: flex;
  align-items: center;

  padding:
    0
    10px;

  border: 0;

  border-bottom:
    1px solid
    rgba(255, 255, 255, 0.16);

  background: #303030;

  color: #dedede;

  cursor: pointer;

  text-align: left;

  transition:
    background-color 0.15s ease,
    color 0.15s ease;
}

/* ============================================================
   HOVER
============================================================ */

.sidebar-item:hover {
  background: #3a3a3a;
}

/* ============================================================
   ACTIVE
============================================================ */

.sidebar-item.active {
  background: #73c5c2;

  color: #ffffff;
}

/*
 * Arrow on the right,
 * matching the dashboard reference.
 */
.sidebar-item.active::after {
  content: "";

  position: absolute;

  top: 12px;
  right: -9px;

  width: 18px;
  height: 18px;

  background: #73c5c2;

  transform: rotate(45deg);

  z-index: 1;
}

/* ============================================================
   ICON
============================================================ */

.sidebar-icon {
  position: relative;

  z-index: 2;

  min-width: 24px;

  margin-right: 7px;

  color: inherit;
}

/* ============================================================
   LABEL
============================================================ */

.sidebar-label {
  position: relative;

  z-index: 2;

  flex: 1;

  font-size: 11px;
  font-weight: 400;

  white-space: nowrap;
}

/* ============================================================
   BADGE
============================================================ */

.sidebar-badge {
  position: relative;

  z-index: 2;

  margin-left: 4px;

  padding:
    1px
    4px;

  border-radius: 3px;

  background: #ff7936;

  color: #ffffff;

  font-size: 7px;
  font-weight: 700;
}

/* ============================================================
   RESPONSIVE
============================================================ */

@media (max-width: 959px) {
  .hosting-sidebar {
    min-height: auto;
  }

  .sidebar-item.active::after {
    display: none;
  }

  .hosting-sidebar-desktop {
  width: 260px;
  min-width: 260px;

  align-self: stretch;

  background: #303030;
}
}
</style>